import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Users, LayoutDashboard, KanbanSquare, MoreHorizontal, Loader, ChevronDown, Calendar, FileText, Check } from 'lucide-react'
import { api } from '../api'

function Sidebar() {
  const location = useLocation()
  const links = [
    { to: '/dashboard', label: 'Clients',  icon: LayoutDashboard },
    { to: '/pipeline',  label: 'Pipeline', icon: KanbanSquare },
    { to: '/team',      label: 'Team',     icon: Users },
  ]
  return (
    <aside style={{ width: 220, background: '#0F172A', color: '#fff', padding: '1.5rem 1rem', display: 'flex', flexDirection: 'column', minHeight: '100vh', flexShrink: 0 }}>
      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: '1.1rem', color: '#3B82F6', marginBottom: '2rem', paddingLeft: '0.5rem' }}>⚡ Prorately</div>
      <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        {links.map(({ to, label, icon: Icon }) => {
          const active = location.pathname === to
          return (
            <Link key={to} to={to} style={{
              display: 'flex', alignItems: 'center', gap: '0.65rem',
              padding: '0.6rem 0.75rem', borderRadius: 8,
              textDecoration: 'none', fontSize: '0.88rem', fontWeight: 500,
              background: active ? '#1E40AF' : 'transparent',
              color: active ? '#fff' : '#94A3B8',
              transition: 'all 0.15s',
            }}>
              <Icon size={16} />{label}
            </Link>
          )
        })}
      </nav>
    </aside>
  )
}

const COLUMNS = [
  { id: 'intake',     label: 'Waiting on Client Intake',   color: '#6366F1', bg: '#EEF2FF' },
  { id: 'agent_info', label: 'Agent Info Needed',           color: '#F59E0B', bg: '#FFFBEB' },
  { id: 'submitted',  label: 'Quote Submitted',             color: '#3B82F6', bg: '#EFF6FF' },
  { id: 'received',   label: 'Quote Received',              color: '#06B6D4', bg: '#ECFEFF' },
  { id: 'awaiting',   label: 'Quoted – Awaiting Decision',  color: '#8B5CF6', bg: '#F5F3FF' },
  { id: 'followup',   label: 'Follow Up / Needs Attention', color: '#EF4444', bg: '#FEF2F2' },
  { id: 'bound',      label: 'Policy Bound 🎉',             color: '#10B981', bg: '#ECFDF5' },
  { id: 'lost',       label: 'Lost / Declined ❌',          color: '#94A3B8', bg: '#F8FAFC' },
]

const EMPTY = Object.fromEntries(COLUMNS.map(c => [c.id, []]))

function isOverdue(dueDate) {
  if (!dueDate) return false
  return new Date(dueDate) < new Date(new Date().toDateString())
}

function isDueSoon(dueDate) {
  if (!dueDate) return false
  const diff = (new Date(dueDate) - new Date(new Date().toDateString())) / (1000 * 60 * 60 * 24)
  return diff >= 0 && diff <= 2
}

function formatDate(d) {
  if (!d) return ''
  const [y, m, day] = d.split('-')
  return `${m}/${day}/${y}`
}

// ── Inline-expandable Card ────────────────────────────────────────────────────
function PipelineCard({ card, colId, colColor, isSaving, onMove, onSaveDetail, menuCard, setMenuCard }) {
  const [expanded, setExpanded]   = useState(false)
  const [editNote, setEditNote]   = useState(card.note || '')
  const [editDate, setEditDate]   = useState(card.dueDate || '')
  const [dirty, setDirty]         = useState(false)
  const [saving, setSaving]       = useState(false)
  const [saved, setSaved]         = useState(false)
  const menuOpen = menuCard?.cardId === card.id
  const overdue  = isOverdue(card.dueDate)
  const soon     = !overdue && isDueSoon(card.dueDate)

  const handleExpand = (e) => {
    e.stopPropagation()
    setExpanded(v => !v)
    setEditNote(card.note || '')
    setEditDate(card.dueDate || '')
    setDirty(false)
    setSaved(false)
  }

  const handleSave = async (e) => {
    e.stopPropagation()
    setSaving(true)
    await onSaveDetail(card.id, { note: editNote, dueDate: editDate })
    setSaving(false)
    setDirty(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div
      className={`card-wrap${isSaving ? ' card-saving' : ''}`}
      style={{ position: 'relative' }}
      onClick={e => e.stopPropagation()}
    >
      {/* ── Card top row ── */}
      <div
        className="card"
        draggable={!isSaving && !expanded}
        onDragStart={e => {
          if (expanded) { e.preventDefault(); return }
          e.dataTransfer.effectAllowed = 'move'
          e.dataTransfer.setData('cardId', card.id)
          e.dataTransfer.setData('colId',  colId)
        }}
        style={{ borderBottomLeftRadius: expanded ? 0 : 10, borderBottomRightRadius: expanded ? 0 : 10, borderBottom: expanded ? 'none' : '1.5px solid #E2E8F0' }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="card-name">{card.name}</div>

          {/* Due date pill on collapsed card */}
          {!expanded && card.dueDate && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              marginTop: '0.3rem', fontSize: '0.7rem', fontWeight: 600,
              color: overdue ? '#DC2626' : soon ? '#D97706' : '#64748B',
              background: overdue ? '#FEF2F2' : soon ? '#FFFBEB' : '#F1F5F9',
              borderRadius: 5, padding: '0.15rem 0.4rem',
            }}>
              <Calendar size={10} />
              {overdue ? '⚠ ' : ''}{formatDate(card.dueDate)}
            </div>
          )}

          {/* Note snippet on collapsed card */}
          {!expanded && card.note && (
            <div style={{ marginTop: '0.25rem', fontSize: '0.72rem', color: '#94A3B8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
              📝 {card.note}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
          {isSaving
            ? <Loader size={13} style={{ animation: 'spin 1s linear infinite', color: '#94A3B8' }} />
            : <>
                <button
                  className="card-icon-btn"
                  title={expanded ? 'Collapse' : 'Expand'}
                  onClick={handleExpand}
                  style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  className="card-icon-btn card-menu-btn"
                  onClick={e => { e.stopPropagation(); setMenuCard(menuOpen ? null : { cardId: card.id, colId }) }}
                >
                  <MoreHorizontal size={14} />
                </button>
              </>
          }
        </div>

        {/* Context menu */}
        {menuOpen && (
          <div className="move-menu" onClick={e => e.stopPropagation()}>
            <div className="move-menu-label">Move to</div>
            {COLUMNS.filter(c => c.id !== colId).map(c => (
              <div key={c.id} className="move-menu-item" onClick={() => onMove(colId, card.id, c.id)}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
                {c.label}
              </div>
            ))}
            <div className="move-menu-sep" />
            <Link to={`/dashboard?client=${card.id}`} className="move-menu-item" onClick={() => setMenuCard(null)} style={{ textDecoration: 'none' }}>
              👤 View client profile
            </Link>
          </div>
        )}
      </div>

      {/* ── Expanded detail panel ── */}
      {expanded && (
        <div className="card-expand" onClick={e => e.stopPropagation()}>

          {/* Due date */}
          <div className="expand-field">
            <label className="expand-label"><Calendar size={11} /> Due Date</label>
            <input
              type="date"
              className={`expand-input${editDate && isOverdue(editDate) ? ' input-overdue' : editDate && isDueSoon(editDate) ? ' input-soon' : ''}`}
              value={editDate}
              onChange={e => { setEditDate(e.target.value); setDirty(true); setSaved(false) }}
            />
            {editDate && isOverdue(editDate) && <span className="overdue-tag">⚠ Overdue</span>}
            {editDate && isDueSoon(editDate) && <span className="soon-tag">Due soon</span>}
          </div>

          {/* Note */}
          <div className="expand-field">
            <label className="expand-label"><FileText size={11} /> Note</label>
            <textarea
              className="expand-textarea"
              placeholder="Add an internal note…"
              value={editNote}
              rows={3}
              onChange={e => { setEditNote(e.target.value); setDirty(true); setSaved(false) }}
            />
          </div>

          {/* Save button */}
          <button
            className={`expand-save${!dirty ? ' expand-save-dim' : ''}`}
            style={{ background: colColor }}
            disabled={!dirty || saving}
            onClick={handleSave}
          >
            {saving
              ? <><Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
              : saved
              ? <><Check size={12} /> Saved</>
              : 'Save changes'
            }
          </button>
        </div>
      )}
    </div>
  )
}

// ── Main Pipeline ─────────────────────────────────────────────────────────────
export default function Pipeline() {
  const [columns, setColumns]   = useState(EMPTY)
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)
  const [menuCard, setMenuCard] = useState(null)
  const [saving, setSaving]     = useState(null)
  const [dragOver, setDragOver] = useState(null)
  const drag = useRef({ cardId: null, fromCol: null })

  useEffect(() => { loadPipeline() }, [])

  const loadPipeline = async () => {
    try {
      setLoading(true); setError(null)
      const data = await api('/api/pipeline')
      setColumns({ ...EMPTY, ...data })
    } catch (err) {
      setError('Failed to load pipeline.')
    } finally {
      setLoading(false)
    }
  }

  const saveStage = async (clientId, newStage) => {
    setSaving(clientId)
    try {
      await api(`/api/pipeline/${clientId}`, { method: 'PATCH', body: JSON.stringify({ stage: newStage }) })
    } catch { await loadPipeline() }
    finally { setSaving(null) }
  }

  const saveDetail = async (clientId, { note, dueDate }) => {
    // optimistic update in columns
    setColumns(prev => {
      const next = { ...prev }
      for (const colId of Object.keys(next)) {
        next[colId] = next[colId].map(c =>
          c.id === clientId ? { ...c, note, dueDate } : c
        )
      }
      return next
    })
    await api(`/api/pipeline/${clientId}`, { method: 'PATCH', body: JSON.stringify({ note, dueDate }) })
  }

  const onDrop = (e, toColId) => {
    e.preventDefault()
    const cardId  = drag.current.cardId  || e.dataTransfer.getData('cardId')
    const fromCol = drag.current.fromCol || e.dataTransfer.getData('colId')
    drag.current = { cardId: null, fromCol: null }
    setDragOver(null)
    if (!cardId || !fromCol || fromCol === toColId) return
    setColumns(prev => {
      const sourceCol = prev[fromCol] || []
      const card = sourceCol.find(c => c.id === cardId)
      if (!card) return prev
      return { ...prev, [fromCol]: sourceCol.filter(c => c.id !== cardId), [toColId]: [...(prev[toColId] || []), card] }
    })
    saveStage(cardId, toColId)
  }

  const moveCard = (fromCol, cardId, toColId) => {
    setMenuCard(null)
    if (fromCol === toColId) return
    setColumns(prev => {
      const sourceCol = prev[fromCol] || []
      const card = sourceCol.find(c => c.id === cardId)
      if (!card) return prev
      return { ...prev, [fromCol]: sourceCol.filter(c => c.id !== cardId), [toColId]: [...(prev[toColId] || []), card] }
    })
    saveStage(cardId, toColId)
  }

  const totalCards = Object.values(columns).reduce((s, col) => s + col.length, 0)

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F1F5F9', fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Sora:wght@700;800&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }

        .pipeline-col { display:flex; flex-direction:column; min-width:240px; max-width:240px; border-radius:14px; flex-shrink:0; }
        .pipeline-col.drag-over { outline:2px dashed #3B82F6; outline-offset:2px; background:#EFF6FF !important; }
        .col-scroll { display:flex; flex-direction:column; gap:0.5rem; min-height:60px; padding:0 0.6rem; }

        .card-wrap { border-radius:10px; overflow:visible; }
        .card-saving { opacity:0.5; pointer-events:none; }
        .card { background:#fff; border-radius:10px; padding:0.7rem 0.75rem; box-shadow:0 1px 3px rgba(15,23,42,0.08); cursor:grab; user-select:none; border:1.5px solid #E2E8F0; transition:border-color 0.15s, box-shadow 0.15s; display:flex; align-items:flex-start; justify-content:space-between; gap:0.4rem; }
        .card:active { cursor:grabbing; }
        .card:hover { border-color:#BFDBFE; box-shadow:0 4px 12px rgba(37,99,235,0.1); }
        .card-name { font-size:0.85rem; font-weight:700; color:#0F172A; line-height:1.3; }

        .card-icon-btn { opacity:0; background:none; border:none; cursor:pointer; padding:0.15rem; border-radius:4px; color:#94A3B8; transition:all 0.15s; flex-shrink:0; display:flex; align-items:center; }
        .card:hover .card-icon-btn { opacity:1; }
        .card-icon-btn:hover { background:#F1F5F9; color:#0F172A; }

        /* Expanded panel */
        .card-expand { background:#fff; border:1.5px solid #E2E8F0; border-top:1px solid #F1F5F9; border-radius:0 0 10px 10px; padding:0.85rem 0.85rem 0.75rem; display:flex; flex-direction:column; gap:0.65rem; box-shadow:0 4px 12px rgba(15,23,42,0.06); }
        .expand-field { display:flex; flex-direction:column; gap:0.3rem; }
        .expand-label { display:flex; align-items:center; gap:0.3rem; font-size:0.7rem; font-weight:700; color:#94A3B8; text-transform:uppercase; letter-spacing:0.06em; }
        .expand-input { border:1.5px solid #E2E8F0; border-radius:7px; padding:0.45rem 0.6rem; font-size:0.82rem; font-family:'DM Sans',sans-serif; color:#0F172A; outline:none; background:#F8FAFF; width:100%; }
        .expand-input:focus { border-color:#3B82F6; background:#fff; box-shadow:0 0 0 3px rgba(59,130,246,0.1); }
        .expand-input.input-overdue { border-color:#FECACA; background:#FEF2F2; color:#DC2626; }
        .expand-input.input-soon    { border-color:#FDE68A; background:#FFFBEB; color:#D97706; }
        .expand-textarea { border:1.5px solid #E2E8F0; border-radius:7px; padding:0.45rem 0.6rem; font-size:0.82rem; font-family:'DM Sans',sans-serif; color:#0F172A; outline:none; background:#F8FAFF; width:100%; resize:vertical; line-height:1.5; }
        .expand-textarea:focus { border-color:#3B82F6; background:#fff; box-shadow:0 0 0 3px rgba(59,130,246,0.1); }
        .expand-save { display:flex; align-items:center; justify-content:center; gap:0.35rem; border:none; border-radius:7px; padding:0.5rem; font-size:0.8rem; font-weight:700; color:#fff; cursor:pointer; transition:all 0.15s; font-family:'DM Sans',sans-serif; }
        .expand-save:hover:not(:disabled) { filter:brightness(1.1); transform:translateY(-1px); }
        .expand-save:disabled { opacity:0.5; cursor:default; }
        .expand-save-dim { opacity:0.5; }
        .overdue-tag { font-size:0.68rem; font-weight:700; color:#DC2626; }
        .soon-tag    { font-size:0.68rem; font-weight:700; color:#D97706; }

        .move-menu { position:absolute; top:calc(100% - 32px); right:0; background:#fff; border:1px solid #E2E8F0; border-radius:10px; box-shadow:0 8px 32px rgba(15,23,42,0.12); z-index:50; min-width:215px; overflow:hidden; }
        .move-menu-label { padding:0.5rem 0.9rem 0.3rem; font-size:0.7rem; font-weight:700; color:#94A3B8; text-transform:uppercase; letter-spacing:0.06em; }
        .move-menu-item { display:flex; align-items:center; gap:0.5rem; padding:0.6rem 0.9rem; font-size:0.82rem; color:#475569; cursor:pointer; transition:background 0.12s; }
        .move-menu-item:hover { background:#F8FAFF; color:#0F172A; }
        .move-menu-sep { height:1px; background:#F1F5F9; margin:0.25rem 0; }

        ::-webkit-scrollbar { height:6px; } ::-webkit-scrollbar-track { background:transparent; } ::-webkit-scrollbar-thumb { background:#CBD5E1; border-radius:10px; }
      `}</style>

      <Sidebar />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '1.5rem 2rem 1rem', background: '#F1F5F9', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <h1 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: '1.4rem', color: '#0F172A', letterSpacing: '-0.02em' }}>Pipeline</h1>
            <p style={{ color: '#64748B', fontSize: '0.85rem', marginTop: '0.2rem' }}>
              {loading ? 'Loading…' : `${totalCards} clients across ${COLUMNS.length} stages`}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button onClick={loadPipeline} style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.82rem', fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
              ↻ Refresh
            </button>
            <div style={{ background: '#0F172A', color: '#fff', borderRadius: 8, padding: '0.5rem 1.1rem', fontSize: '0.82rem', fontWeight: 700, fontFamily: "'Sora',sans-serif" }}>
              {(columns.bound || []).length} Bound this cycle
            </div>
          </div>
        </div>

        {error && (
          <div style={{ background: '#FEF2F2', borderBottom: '1px solid #FECACA', padding: '0.75rem 2rem', color: '#DC2626', fontSize: '0.85rem', display: 'flex', gap: '0.5rem' }}>
            ⚠ {error}
            <button onClick={loadPipeline} style={{ background: 'none', border: 'none', color: '#DC2626', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
          </div>
        )}

        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', color: '#64748B' }}>
            <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> Loading pipeline…
          </div>
        ) : (
          <div
            style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', padding: '1.5rem 2rem 2rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}
            onClick={() => setMenuCard(null)}
          >
            {COLUMNS.map(col => {
              const cards = columns[col.id] || []
              const isOver = dragOver === col.id
              return (
                <div
                  key={col.id}
                  className={`pipeline-col${isOver ? ' drag-over' : ''}`}
                  style={{ background: col.bg }}
                  onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(col.id) }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={e => onDrop(e, col.id)}
                >
                  {/* Column header */}
                  <div style={{ padding: '0.85rem 0.9rem 0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: col.color, flexShrink: 0, display: 'inline-block' }} />
                      <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: '0.75rem', color: '#0F172A', lineHeight: 1.3 }}>{col.label}</span>
                    </div>
                    <span style={{ background: col.color, color: '#fff', fontSize: '0.68rem', fontWeight: 700, borderRadius: 100, padding: '0.1rem 0.45rem', minWidth: 20, textAlign: 'center' }}>{cards.length}</span>
                  </div>

                  {/* Cards */}
                  <div className="col-scroll">
                    {cards.map(card => (
                      <PipelineCard
                        key={card.id}
                        card={card}
                        colId={col.id}
                        colColor={col.color}
                        isSaving={saving === card.id}
                        onMove={moveCard}
                        onSaveDetail={saveDetail}
                        menuCard={menuCard}
                        setMenuCard={setMenuCard}
                      />
                    ))}
                  </div>
                  <div style={{ height: '0.75rem' }} />
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}